export type WalletAccountSummary = {
  id: string;
  walletAccountNumber: string;
  name: string | null;
  status: string;
  createdAt: string | null;
  updatedAt: string | null;
  latestWithdrawal: {
    id: string;
    amount: string | number;
    currency: string;
    status: string;
    createdAt: string | null;
  } | null;
  balance?: {
    available: string;
    balance: string;
    currency?: string | null;
  } | null;
  balanceUnavailableReason?: string | null;
};

export type WalletSummaryResponse = {
  appUser: {
    id: string;
    uuid: string | null;
    email: string | null;
    username: string | null;
    status: string | null;
    walletProfileId: string | null;
  } | null;
  walletAccounts: WalletAccountSummary[];
  summary?: {
    walletAccountCount: number;
    activeWalletAccountCount: number;
    providerBalanceAvailable: boolean;
    providerBalanceCount?: number;
    totalAvailableBalance?: string | number | null;
    currency?: string | null;
  };
  migrationStatus?: { state?: string; reason?: string };
};

export type WalletWithdrawalRiskResponse = {
  appUser?: {
    id: string;
    uuid: string;
    username: string | null;
    email: string | null;
    status: string | null;
  };
  wallet?: {
    id: string;
    walletAccountNumber: string;
    status: string;
    createdAt: string | null;
  };
  hold?: {
    id: string;
    status: 'ACTIVE' | 'RELEASED' | string;
    reasonCodes: string[];
    evidence: Array<{
      reasonCode: string;
      count: number;
      detectedAt: string;
      signalHash?: string;
    }>;
    detectedAt: string;
    lastDetectedAt: string;
    releasedAt: string | null;
    releasedBy: string | null;
    releaseReason: string | null;
    waiverUntil: string | null;
  } | null;
  evidenceSummary?: {
    reasonCodes: string[];
    evidenceCount: number;
  } | null;
};

export type WalletWithdrawal = {
  id: string;
  amount: string | number;
  currency: string;
  status: string;
  payout_channel: string | null;
  retailer: string | null;
  provider_reference: string | null;
  provider_request_id: string | null;
  voucher_expires_at: string | null;
  failure_code: string | null;
  failure_message: string | null;
  created_at: string | null;
  updated_at: string | null;
  voucher_code_available: boolean;
  voucher_pin_available: boolean;
  voucher_code_masked?: string | null;
  voucher_pin_masked?: string | null;
  wallet_account_number: string | null;
  wallet_name: string | null;
  wallet_status: string | null;
};

export type WalletWithdrawalCounts = {
  totalWithdrawals: number;
  pendingWithdrawals: number;
  failedWithdrawals: number;
};

export type WalletWithdrawalsResponse = {
  summaryByWallet?: Record<string, WalletWithdrawalCounts>;
  listLimit?: number;
  withdrawals: WalletWithdrawal[];
  summary?: {
    totalWithdrawals: number;
    pendingWithdrawals: number;
    failedWithdrawals: number;
  };
  migrationStatus?: { state?: string; reason?: string };
};

/**
 * Who the wallet pane is about. Wallets belong to an app user, so the app-user
 * UUID is the preferred selector; the legacy i_customer resolves through the
 * account's linked app users on the server. `walletAccountNumber` narrows the
 * pane to one wallet when the agent arrived through a wallet search.
 */
export interface WalletScope {
  mvno?: string | null;
  appUserUuid?: string | null;
  iCustomer?: number | null;
  walletAccountNumber?: string | null;
}

export const walletSelectorQuery = ({
  iCustomer,
  mvno,
  appUserUuid,
}: WalletScope) => {
  const params = new URLSearchParams();
  if (appUserUuid) params.set('app_user_uuid', appUserUuid);
  else if (iCustomer) params.set('i_customer', String(iCustomer));
  if (mvno) params.set('mvno', mvno);
  return `?${params.toString()}`;
};

export const hasWalletSelector = (scope: WalletScope) =>
  Boolean(scope.appUserUuid || scope.iCustomer);
